import type { SSEMessage } from '@/_shared/types';
import type { ChatDashboardArtifactsStore, DashboardArtifactSnapshot } from '@/chat/hooks/useChatDashboardArtifacts';
import {
  DASHBOARD_PREVIEW_TOOL,
  DASHBOARD_SAVE_TOOL,
  DASHBOARD_UPDATE_TOOL,
  dashboardDraftFromToolCall,
  dashboardToolCallSlug,
  isSuccessfulPreviewResult,
  parseDashboardToolResultSummary,
  parseSaveDashboardResult,
  previewArgsToDraft,
} from '@/chat/lib/dashboard-draft';

/**
 * Adopt completed preview/save/update tool results into the live artifact store.
 * Returns the set of tool ids that were processed (caller merges into a ref).
 */
export function adoptDashboardToolResults(
  sseMessages: SSEMessage[],
  store: Pick<
    ChatDashboardArtifactsStore,
    'artifacts' | 'syncAfterSave' | 'syncFromTranscript' | 'markSaved' | 'clearServerPersistPending'
  >,
  alreadyProcessed: ReadonlySet<string>,
): Set<string> {
  const newlyProcessed = new Set<string>();
  // A patch call replays over the live artifact. The draft event usually landed it already; the replay
  // is idempotent, so it also covers a client that missed that ephemeral event. `store.artifacts` does
  // not refresh inside this loop, so drafts adopted earlier in the batch are tracked here — otherwise
  // a second patch to the same slug would replay over the pre-first-patch copy and drop that edit. A
  // replay over a copy that is already ahead keeps the tool's panel order through the result's
  // `panelIds` (`dashboardDraftFromToolCall`). An empty card shell (`hydrateSavedAndOpen`, when the
  // draft event was missed) is no base: `dashboardDraftFromToolCall` refuses it rather than turn the
  // patched panels into the whole dashboard.
  const adoptedInBatch = new Map<string, DashboardArtifactSnapshot>();
  const liveSnapshot = (args: unknown) => {
    const slug = dashboardToolCallSlug(args);
    return slug === null ? undefined : (adoptedInBatch.get(slug) ?? store.artifacts[slug]);
  };
  const toolResults = new Map<string, Extract<SSEMessage, { type: 'tool_result' }>>();
  for (const msg of sseMessages) {
    if (msg.type === 'tool_result') toolResults.set(msg.id, msg);
  }

  for (const msg of sseMessages) {
    if (msg.type !== 'tool_use') continue;
    if (alreadyProcessed.has(msg.id) || newlyProcessed.has(msg.id)) continue;

    const resultMsg = toolResults.get(msg.id);
    if (!resultMsg) continue;

    if (msg.name === DASHBOARD_UPDATE_TOOL) {
      newlyProcessed.add(msg.id);
      if (!isSuccessfulPreviewResult(resultMsg.content)) {
        const slug = dashboardToolCallSlug(msg.args);
        if (slug) store.clearServerPersistPending(slug);
        continue;
      }
      const savedRef = parseSaveDashboardResult(resultMsg.content);
      const draft = dashboardDraftFromToolCall(
        msg.args,
        liveSnapshot(msg.args),
        parseDashboardToolResultSummary(resultMsg.content),
      );
      if (draft) adoptedInBatch.set(draft.slug, draft);
      if (draft && savedRef) {
        // Updates carry a real in-flight marker (origin:'update' set pendingPersistPatchSeq at
        // dispatch); keep it so a draft that advanced past the update stays dirty for auto-save.
        const savedPatchSeq = store.artifacts[draft.slug]?.pendingPersistPatchSeq;
        store.syncAfterSave(draft.slug, draft, savedRef, savedPatchSeq);
      }
      continue;
    }

    if (msg.name === DASHBOARD_PREVIEW_TOOL) {
      newlyProcessed.add(msg.id);
      if (!isSuccessfulPreviewResult(resultMsg.content)) continue;
      const draft = dashboardDraftFromToolCall(
        msg.args,
        liveSnapshot(msg.args),
        parseDashboardToolResultSummary(resultMsg.content),
      );
      if (draft) {
        adoptedInBatch.set(draft.slug, draft);
        store.syncFromTranscript(draft);
      }
      continue;
    }

    if (msg.name === DASHBOARD_SAVE_TOOL) {
      newlyProcessed.add(msg.id);
      const savedRef = parseSaveDashboardResult(resultMsg.content);
      if (!savedRef) continue;
      const draft = previewArgsToDraft(msg.args);
      if (draft) {
        const existing = store.artifacts[draft.slug];
        // Fall back to the current patchSeq so a save with no in-flight marker (agent save_dashboard,
        // or a late adopt) still advances lastPersistedPatchSeq — otherwise the artifact stays
        // auto-save-eligible and fires a redundant update_dashboard right after every save.
        const savedPatchSeq = existing?.pendingPersistPatchSeq ?? existing?.patchSeq;
        store.syncAfterSave(draft.slug, draft, savedRef, savedPatchSeq);
      } else {
        store.markSaved(savedRef.slug, savedRef);
      }
    }
  }

  return newlyProcessed;
}
